import { DeleteRounded, EditRounded } from '@mui/icons-material';
import { Box } from '@mui/system';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { TableComponent } from 'app/shared/components/table-component/TableComponent';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { IVehicleModel } from 'app/shared/model/vehicle-model.model';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import React, { useCallback, useEffect, useState } from 'react';
import { getSortState, Translate, translate } from 'react-jhipster';
import { RouteComponentProps } from 'react-router-dom';
import '../../modules/vehicle-model/vehicleModel.scss';
import { getVehicleModelSummary } from './vehicle-model.reducer';

export const VehicleModel = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();

  const totalItems = useAppSelector(state => state.vehicleModel.totalItems);
  const loading = useAppSelector(state => state.vehicleModel.loading);
  const updateSuccess = useAppSelector(state => state.vehicleModel.updateSuccess);
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'name'), props.location.search)
  );
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

  const vehicleModelList = useAppSelector(state => state.vehicleModel.entities);
  const [nameSearch, setNameSearch] = useState<string>('');

  const headers: IHeaderItem[] = [
    {
      key: 'name',
      sortable: true,
      component: <Translate contentKey="fleetSenseApp.vehicleModel.name" />,
    },
    {
      key: 'category',
      sortable: true,
      component: <Translate contentKey="fleetSenseApp.vehicleModel.category" />,
    },
    {
      key: 'subcategory',
      sortable: true,
      component: <Translate contentKey="fleetSenseApp.vehicleModel.subcategory" />,
    },
    {
      key: 'type',
      sortable: true,
      component: <Translate contentKey="fleetSenseApp.vehicleModel.type" />,
    },
    {
      key: 'image',
      sortable: false,
      component: <Translate contentKey="fleetSenseApp.vehicleModel.image" />,
    },
  ];

  const groupButtonIcons = [
    {
      icon: <EditRounded color="secondary" />,
      path: 'edit',
    },
    {
      icon: <DeleteRounded color="error" />,
      path: 'delete',
    },
  ];

  const data: IListData[] = [
    {
      type: 'text',
      value: 'name',
    },
    {
      type: 'translateText',
      value: 'category',
      translate: 'fleetSenseApp.CategoryVehicleModel',
    },
    {
      type: 'translateText',
      value: 'subcategory',
      translate: 'fleetSenseApp.subcategoryVehicleModel',
    },
    {
      type: 'translateText',
      value: 'type',
      translate: 'fleetSenseApp.VehicleTypeEnum',
    },
    {
      type: 'image',
      value: 'image',
    },
    {
      type: 'groupButtonIcons',
      value: 'id',
    },
  ];

  const searchTypes: ISearchTypes[] = [
    {
      label: translate('fleetSenseApp.vehicleModel.name'),
      placeholder: translate('fleetSenseApp.maintenanceModel.search'),
      value: nameSearch,
      setValue: setNameSearch,
    },
  ];

  const getAllEntities = useCallback(() => {
    dispatch(
      getVehicleModelSummary({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        nameSearch,
      })
    );

    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  }, [paginationState, updateSuccess]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = (_, page) =>
    setPaginationState({
      ...paginationState,
      activePage: page,
    });

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  const { match } = props;

  return (
    <Box sx={{ backgroundColor: 'primary.main', borderRadius: '8px', paddingTop: '16px', textAlign: 'center', padding: '24px' }}>
      <PageHeader
        headingId="vehicle-model-heading"
        headingDataCy="VehicleModelHeading"
        contentKey="fleetSenseApp.vehicleModel.home.title"
        buttonTextKey="fleetSenseApp.vehicleModel.home.createLabel"
        match={match}
        showButton={isAdmin}
      />

      <PageFilters setActivePage={setActivePage} searchTypes={searchTypes} showFilters={['filters']} />

      {vehicleModelList?.length && !loading && (
        <TableComponent
          listData={data}
          headers={headers}
          list={vehicleModelList}
          match={match}
          sortFunction={sort}
          pagination={paginationState}
          groupButtonIcons={groupButtonIcons}
        />
      )}
      {!loading && !vehicleModelList?.length && <NoData title="vehicleModel" />}
      <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={vehicleModelList} />
    </Box>
  );
};

export default VehicleModel;
